import { contactEventData } from "components/contacts/cognism/contactTracking";
import { ContactChannelList } from "components/contacts/contact_channels/ContactChannelList";
import { visibleChannels } from "components/contacts/contact_channels/contactChannels";
import { SendContactToCRM } from "components/integrations/SendContactToCRMButton";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import { LeadDetails } from "lib/hooks/api/leads/useLead";
import { useSubscription } from "lib/hooks/subscription";
import { ContactHint, LeadContactCard } from "./LeadContactCard";

/** A contact as the lead query returns it — a superset of the CRM button's `ContactData`. */
export type SavedContact = NonNullable<LeadDetails>["contacts"][number];

/**
 * A contact already attached to the lead, in the same card as a suggestion (see
 * {@link LeadContactCard}) — solid border rather than dashed, and its actions behind a "⋯" menu
 * instead of accept/dismiss.
 *
 * Details need no reveal: the lead query returns them for both sources (attaching a contact
 * reveals it, so a saved contact is one this company already holds and the read is free).
 */
export function LeadSavedContactCard({
  contact,
  onOutreachClick,
  onRemove,
}: {
  contact: SavedContact;
  onOutreachClick: () => void;
  onRemove: () => void;
}) {
  const subscription = useSubscription();
  const hasContactsAccess = subscription.hasDataTypes("CONTACTS");

  const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() || "Contact";
  const jobFunction = contact.jobFunction?.length ? contact.jobFunction.join(", ") : "-";

  return (
    <LeadContactCard
      variant="saved"
      ariaLabel="Saved contact card"
      name={name}
      jobTitle={contact.jobTitle}
      // Saved contacts carry the source's own seniority string; Cognism doesn't supply one yet.
      seniority={contact.seniority?.trim() || "-"}
      jobFunction={jobFunction}
      buyer={contact.buyer ?? null}
      actions={
        <DropdownMenu
          trigger={
            <DropdownMenuTrigger
              buttonStyle="inverse"
              buttonType="primary"
              icon="dotsHorizontal"
              aria-label="Contact actions"
              hideArrow
            />
          }
          align="end"
          width={200}
        >
          {hasContactsAccess && (
            <DropdownMenuItem
              key="outreach"
              icon="send"
              onClick={onOutreachClick}
              textValue="Generate outreach"
            >
              Generate outreach
            </DropdownMenuItem>
          )}
          {/* Rendered as-is rather than as a DropdownMenuItem: this button owns the provider
              lookup, the already-in-CRM "View" variant, the paywall and the tracking, none of
              which should be reimplemented to fit a menu item's shape. */}
          {hasContactsAccess && (
            <SendContactToCRM
              contact={contact}
              buttonType="primary"
              buttonStyle="inverse"
              fullWidth
              justifyContent="left"
              showingTour={false}
            />
          )}
          <DropdownMenuItem
            key="remove"
            icon="trash01"
            onClick={onRemove}
            textValue="Remove from lead"
          >
            Remove from lead
          </DropdownMenuItem>
        </DropdownMenu>
      }
      details={<SavedContactChannels contact={contact} />}
    />
  );
}

/**
 * A saved contact's channels, in the same lines the contacts table renders. Nothing is revealed
 * here: the lead query returns the details for both sources (attaching a contact reveals it, so
 * a saved contact is one this company already holds and the read is free).
 */
function SavedContactChannels({ contact }: { contact: SavedContact }) {
  const rows = visibleChannels(contact, true);
  if (rows.length === 0) {
    return <ContactHint>No contact details available for this contact</ContactHint>;
  }
  return (
    <ContactChannelList
      rows={rows}
      valueTone="default"
      trackingData={contactEventData({
        source: String(contact.source),
        jobTitle: contact.jobTitle,
        // Saved contacts carry the source's own seniority string rather than a tier key.
        seniorityTier: contact.seniority,
        jobFunction: contact.jobFunction,
        buyer: contact.buyer,
        oscarOrgName: contact.oscarOrgName,
      })}
    />
  );
}
